/**
 * Beschikbaarheid Dashboard — Overzicht personeelsbezetting & capaciteit
 * Toont per dag wie er werkt, verlof heeft, en wat de showroom-capaciteit is.
 * Inclusief tijdslot-weergave en boekingssysteem gereedheidscheck.
 */
import React, { useState, useMemo, useCallback } from 'react';
import { useApp } from '../contexts/AppContext';
import { getBeschikbaarheidAllemaal, getBeschikbaarheidDag, checkCapaciteit, getWerkBlokken } from '../services/beschikbaarheidService';

const STATUS_KLEUREN = {
  beschikbaar: '#107C10', verlof: '#D83B01', ziek: '#D13438',
  vrij: '#c8c6c4', feestdag: '#8764B8', flexadv: '#0078D4',
};
const STATUS_BG = {
  beschikbaar: '#dff6dd', verlof: '#fde7e9', ziek: '#fde7e9',
  vrij: '#f3f2f1', feestdag: '#f3e8ff', flexadv: '#e8f4fd',
};
const STATUS_ICONS = {
  beschikbaar: '🟢', verlof: '🔴', ziek: '🏥', vrij: '⚪', feestdag: '🎉', flexadv: '🔵',
};

export default function BeschikbaarheidDashboard() {
  const { medewerkers, verlofaanvragen, roosterWijzigingen, feestdagen } = useApp();
  const [weken, setWeken] = useState(2);
  const [minimumBezetting, setMinimumBezetting] = useState(2);
  const [toonAlleen, setToonAlleen] = useState('alle');
  const [geselecteerdeDag, setGeselecteerdeDag] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // grid | tijdslot | boekingapi

  const actieveMw = useMemo(() =>
    medewerkers.filter(m => (m.Status || 'actief') === 'actief' && m.Rol !== 'flexkracht'),
    [medewerkers]
  );
  const showroomMw = useMemo(() => actieveMw.filter(m => m.ShowroomMedewerker), [actieveMw]);
  const filterMw = toonAlleen === 'showroom' ? showroomMw : actieveMw;
  const showroomIds = showroomMw.map(m => m.Id);

  const datums = useMemo(() => {
    const result = [];
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const start = new Date(today);
    const dow = start.getDay();
    start.setDate(start.getDate() - (dow === 0 ? 6 : dow - 1));
    for (let i = 0; i < weken * 7; i++) {
      const d = new Date(start); d.setDate(d.getDate() + i);
      result.push(d.toISOString().split('T')[0]);
    }
    return result;
  }, [weken]);

  const beschikbaarheidData = useMemo(() => {
    const data = {};
    for (const datum of datums) {
      const d = new Date(datum + 'T12:00:00');
      if (d.getDay() === 0 || d.getDay() === 6) continue;
      const alle = getBeschikbaarheidAllemaal(medewerkers, datum, verlofaanvragen, roosterWijzigingen, feestdagen);
      const capaciteit = checkCapaciteit(medewerkers, datum, verlofaanvragen, roosterWijzigingen, feestdagen, minimumBezetting, showroomIds.length > 0 ? showroomIds : null);
      data[datum] = { alle, capaciteit };
    }
    return data;
  }, [datums, medewerkers, verlofaanvragen, roosterWijzigingen, feestdagen, minimumBezetting, showroomIds]);

  const wekelijks = useMemo(() => {
    const weeks = [];
    for (let w = 0; w < weken; w++) {
      const weekDatums = datums.slice(w * 7, (w + 1) * 7).filter(d => {
        const day = new Date(d + 'T12:00:00').getDay();
        return day !== 0 && day !== 6;
      });
      if (weekDatums.length > 0) weeks.push(weekDatums);
    }
    return weeks;
  }, [datums, weken]);

  const today = new Date().toISOString().split('T')[0];
  const dagDetail = geselecteerdeDag ? beschikbaarheidData[geselecteerdeDag] : null;

  // Tijdslot analyse voor geselecteerde dag
  const tijdslotData = useMemo(() => {
    if (!geselecteerdeDag || !dagDetail) return null;
    const slots = [
      { label: 'Ochtend', start: '08:00', eind: '12:30', icon: '🌅' },
      { label: 'Middag', start: '12:30', eind: '17:30', icon: '☀️' },
    ];
    return slots.map(slot => {
      const beschikbaar = dagDetail.alle.filter(b => {
        if (!b.beschikbaar || !b.werkTijden) return false;
        const werkStart = b.werkTijden.startTijd;
        const werkEind = b.werkTijden.eindTijd;
        return werkStart < slot.eind && werkEind > slot.start;
      });
      const showroomBeschikbaar = beschikbaar.filter(b => showroomIds.includes(b.medewerker.Id));
      return {
        ...slot,
        beschikbaar,
        showroomBeschikbaar,
        capaciteitOk: beschikbaar.length >= minimumBezetting,
        showroomOk: showroomMw.length === 0 || showroomBeschikbaar.length >= 1,
      };
    });
  }, [geselecteerdeDag, dagDetail, minimumBezetting, showroomIds, showroomMw.length]);

  // Boekings-API data export
  const handleExportApi = useCallback(() => {
    const apiData = {
      generatedAt: new Date().toISOString(),
      periodeWeken: weken,
      minimumBezetting,
      showroomMedewerkers: showroomMw.map(m => ({ id: m.Id, naam: m.Naam })),
      beschikbaarheid: {},
    };
    Object.entries(beschikbaarheidData).forEach(([datum, data]) => {
      const slots = [
        { slot: 'ochtend', start: '08:00', eind: '12:30' },
        { slot: 'middag', start: '12:30', eind: '17:30' },
      ];
      apiData.beschikbaarheid[datum] = {
        datum,
        weekdag: new Date(datum + 'T12:00:00').toLocaleDateString('nl-NL', { weekday: 'long' }),
        totaalBeschikbaar: data.capaciteit.aantalBeschikbaar,
        totaalMedewerkers: data.capaciteit.totaalMedewerkers,
        capaciteitOk: data.capaciteit.beschikbaar,
        showroomBeschikbaar: data.alle.filter(b => b.beschikbaar && showroomIds.includes(b.medewerker.Id)).length,
        tijdslots: slots.map(s => {
          const inSlot = data.alle.filter(b => b.beschikbaar && b.werkTijden && b.werkTijden.startTijd < s.eind && b.werkTijden.eindTijd > s.start);
          return {
            slot: s.slot,
            start: s.start,
            eind: s.eind,
            beschikbaar: inSlot.length,
            medewerkers: inSlot.map(b => ({ id: b.medewerker.Id, naam: b.medewerker.Naam, showroom: showroomIds.includes(b.medewerker.Id) })),
            bookbaar: inSlot.length >= minimumBezetting,
          };
        }),
        medewerkers: data.alle.map(b => ({
          id: b.medewerker.Id, naam: b.medewerker.Naam,
          status: b.status, werkTijden: b.werkTijden,
          showroom: b.medewerker.ShowroomMedewerker || false,
        })),
      };
    });
    const blob = new Blob([JSON.stringify(apiData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `beschikbaarheid-api-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [beschikbaarheidData, weken, minimumBezetting, showroomMw, showroomIds]);

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1a1a2e', margin: 0 }}>👥 Beschikbaarheid & Capaciteit</h3>
          <p style={{ fontSize: 12, color: '#605e5c', margin: '4px 0 0' }}>
            Personeelsbezetting per dag en tijdslot. Basis voor boekingssysteem showroombezoeken.
          </p>
        </div>
      </div>

      {/* View mode tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '2px solid #edebe9', paddingBottom: 0 }}>
        {[
          { key: 'grid', label: '📊 Overzicht' },
          { key: 'tijdslot', label: '🕐 Tijdslots' },
          { key: 'boekingapi', label: '🔗 Boekingssysteem' },
        ].map(v => (
          <button key={v.key} onClick={() => setViewMode(v.key)}
            style={{ padding: '8px 16px', border: 'none', background: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              color: viewMode === v.key ? '#E86E1C' : '#605e5c', borderBottom: viewMode === v.key ? '2px solid #E86E1C' : '2px solid transparent', marginBottom: -2 }}>
            {v.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16, padding: '12px 16px', background: '#faf9f8', borderRadius: 10, alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#605e5c' }}>Periode:</label>
          {[2, 4, 8].map(w => (
            <button key={w} onClick={() => setWeken(w)}
              style={{ padding: '4px 12px', borderRadius: 6, border: weken === w ? '2px solid #0078D4' : '1px solid #c8c6c4',
                background: weken === w ? '#e8f4fd' : '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: weken === w ? '#0078D4' : '#605e5c' }}>
              {w} wkn
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#605e5c' }}>Filter:</label>
          <button onClick={() => setToonAlleen('alle')}
            style={{ padding: '4px 12px', borderRadius: 6, border: toonAlleen === 'alle' ? '2px solid #0078D4' : '1px solid #c8c6c4',
              background: toonAlleen === 'alle' ? '#e8f4fd' : '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: toonAlleen === 'alle' ? '#0078D4' : '#605e5c' }}>
            Alle ({actieveMw.length})
          </button>
          <button onClick={() => setToonAlleen('showroom')}
            style={{ padding: '4px 12px', borderRadius: 6, border: toonAlleen === 'showroom' ? '2px solid #E86E1C' : '1px solid #c8c6c4',
              background: toonAlleen === 'showroom' ? '#fff4e6' : '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: toonAlleen === 'showroom' ? '#E86E1C' : '#605e5c' }}>
            🏪 Showroom ({showroomMw.length})
          </button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#605e5c' }}>Min. bezetting:</label>
          <input type="number" min="1" max="20" value={minimumBezetting}
            onChange={e => setMinimumBezetting(Math.max(1, Number(e.target.value)))}
            style={{ width: 50, padding: '4px 8px', borderRadius: 6, border: '1px solid #c8c6c4', fontSize: 12, textAlign: 'center' }} />
        </div>
      </div>

      {showroomMw.length === 0 && (
        <div style={{ background: '#fff4ce', border: '1px solid #e6d272', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: '#7A6400' }}>
          ⚠️ <strong>Geen showroom-medewerkers.</strong> Markeer via Medewerkers → 🏪 Showroom. Vereist voor boekingssysteem.
        </div>
      )}

      {/* Legenda */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 16, flexWrap: 'wrap', fontSize: 11, color: '#605e5c' }}>
        <span>🟢 Beschikbaar</span><span>🔴 Verlof</span><span>🏥 Ziek</span>
        <span>🔵 Flex/ADV</span><span>🎉 Feestdag</span><span>⚪ Vrij</span>
        <span style={{ borderLeft: '1px solid #edebe9', paddingLeft: 12 }}>
          ✅ ≥ minimum · ⚠️ onder minimum · 🚫 geen capaciteit
        </span>
      </div>

      {/* ===== GRID VIEW ===== */}
      {viewMode === 'grid' && wekelijks.map((weekDatums, weekIdx) => (
        <div key={weekIdx} style={{ marginBottom: 16, border: '1px solid #edebe9', borderRadius: 10, overflow: 'auto' }}>
          <div style={{ background: '#faf9f8', padding: '8px 16px', fontSize: 12, fontWeight: 700, color: '#605e5c' }}>
            Week {getWeekNummer(weekDatums[0])}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: `minmax(120px, 150px) repeat(${weekDatums.length}, 1fr)`, gap: 1, background: '#edebe9', padding: 1 }}>
            {/* Header */}
            <div style={{ background: '#faf9f8', padding: '6px 10px', fontSize: 11, fontWeight: 600, color: '#605e5c' }}>Medewerker</div>
            {weekDatums.map(datum => {
              const d = beschikbaarheidData[datum];
              const cap = d?.capaciteit;
              const isToday = datum === today;
              const capIcon = !cap ? '' : cap.aantalBeschikbaar === 0 ? '🚫' : !cap.beschikbaar ? '⚠️' : '✅';
              return (
                <div key={datum} onClick={() => setGeselecteerdeDag(geselecteerdeDag === datum ? null : datum)}
                  style={{ background: isToday ? '#fff8e8' : '#faf9f8', padding: '4px 6px', textAlign: 'center', cursor: 'pointer',
                    borderBottom: geselecteerdeDag === datum ? '3px solid #0078D4' : 'none' }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: isToday ? '#E86E1C' : '#605e5c' }}>
                    {new Date(datum + 'T12:00:00').toLocaleDateString('nl-NL', { weekday: 'short' })}
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#323130' }}>
                    {new Date(datum + 'T12:00:00').getDate()}
                  </div>
                  <div style={{ fontSize: 10 }}>{capIcon} {cap?.aantalBeschikbaar || 0}/{cap?.totaalMedewerkers || 0}</div>
                </div>
              );
            })}

            {/* Medewerker rijen */}
            {filterMw.map(mw => (
              <React.Fragment key={mw.Id}>
                <div style={{ background: '#fff', padding: '4px 10px', fontSize: 12, fontWeight: 600, color: '#323130', display: 'flex', alignItems: 'center', gap: 4, overflow: 'hidden', whiteSpace: 'nowrap' }}>
                  {mw.ShowroomMedewerker && <span style={{ fontSize: 10 }}>🏪</span>}
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{mw.Naam}</span>
                </div>
                {weekDatums.map(datum => {
                  const mwStatus = beschikbaarheidData[datum]?.alle?.find(b => b.medewerker?.Id === mw.Id);
                  const status = mwStatus?.status || 'vrij';
                  const icon = STATUS_ICONS[status] || '⚪';
                  const hasVerlof = mwStatus?.isVerlof && mwStatus?.werkt;
                  return (
                    <div key={datum}
                      title={`${mw.Naam} — ${mwStatus?.statusLabel || 'Vrij'}${mwStatus?.werkTijden ? ` (${mwStatus.werkTijden.startTijd}-${mwStatus.werkTijden.eindTijd})` : ''}`}
                      style={{ padding: '4px', textAlign: 'center', borderRadius: 4, fontSize: 12,
                        background: STATUS_BG[status] || '#f3f2f1',
                        border: hasVerlof ? `2px solid ${STATUS_KLEUREN[status]}` : `1px solid ${STATUS_KLEUREN[status] || '#edebe9'}30`,
                        cursor: 'default' }}>
                      {icon}
                      {status === 'beschikbaar' && mwStatus?.werkTijden && (
                        <div style={{ fontSize: 8, color: '#605e5c', marginTop: 1 }}>
                          {mwStatus.werkTijden.startTijd?.substring(0, 5)}-{mwStatus.werkTijden.eindTijd?.substring(0, 5)}
                        </div>
                      )}
                      {hasVerlof && mwStatus?.verlofItems?.[0] && (
                        <div style={{ fontSize: 7, color: STATUS_KLEUREN[status], fontWeight: 700, marginTop: 1 }}>
                          {mwStatus.verlofItems[0].Type?.substring(0, 4)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      ))}

      {/* ===== TIJDSLOT VIEW ===== */}
      {viewMode === 'tijdslot' && (
        <div>
          <p style={{ fontSize: 12, color: '#605e5c', marginBottom: 16 }}>
            Selecteer een dag in het overzicht hierboven of klik op een datum hieronder voor tijdslot-analyse.
          </p>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
            {Object.keys(beschikbaarheidData).map(datum => {
              const cap = beschikbaarheidData[datum]?.capaciteit;
              const isSelected = datum === geselecteerdeDag;
              return (
                <button key={datum} onClick={() => setGeselecteerdeDag(isSelected ? null : datum)}
                  style={{ padding: '6px 12px', borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                    border: isSelected ? '2px solid #0078D4' : '1px solid #edebe9',
                    background: isSelected ? '#e8f4fd' : !cap?.beschikbaar ? '#fde7e9' : '#fff',
                    color: isSelected ? '#0078D4' : '#323130' }}>
                  {new Date(datum + 'T12:00:00').toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' })}
                  <span style={{ marginLeft: 4 }}>{!cap ? '' : cap.aantalBeschikbaar === 0 ? '🚫' : !cap.beschikbaar ? '⚠️' : '✅'}</span>
                </button>
              );
            })}
          </div>

          {tijdslotData && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
              {tijdslotData.map(slot => (
                <div key={slot.label} style={{ border: `2px solid ${slot.capaciteitOk ? '#a7e3a5' : '#f1b1b5'}`, borderRadius: 12, overflow: 'hidden' }}>
                  <div style={{ background: slot.capaciteitOk ? '#dff6dd' : '#fde7e9', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 14, fontWeight: 700 }}>{slot.icon} {slot.label} ({slot.start}-{slot.eind})</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: slot.capaciteitOk ? '#107C10' : '#D13438' }}>
                      {slot.capaciteitOk ? '✅ Bookbaar' : '🚫 Niet bookbaar'}
                    </span>
                  </div>
                  <div style={{ padding: 12 }}>
                    <div style={{ fontSize: 12, marginBottom: 8 }}>
                      <strong>{slot.beschikbaar.length}</strong> beschikbaar
                      {showroomMw.length > 0 && <span> · <strong>{slot.showroomBeschikbaar.length}</strong> 🏪 showroom</span>}
                    </div>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {slot.beschikbaar.map(b => (
                        <span key={b.medewerker.Id} style={{ padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                          background: showroomIds.includes(b.medewerker.Id) ? '#fff4e6' : '#dff6dd',
                          border: `1px solid ${showroomIds.includes(b.medewerker.Id) ? '#ffe0b2' : '#a7e3a5'}` }}>
                          {showroomIds.includes(b.medewerker.Id) && '🏪 '}{b.medewerker.Naam}
                        </span>
                      ))}
                      {slot.beschikbaar.length === 0 && <span style={{ fontSize: 11, color: '#D13438', fontStyle: 'italic' }}>Geen medewerkers beschikbaar</span>}
                    </div>
                    {!slot.capaciteitOk && (
                      <div style={{ marginTop: 8, fontSize: 11, color: '#D13438', background: '#fde7e9', padding: '4px 8px', borderRadius: 4 }}>
                        ⚠️ Onvoldoende capaciteit — boekingssysteem blokkeert dit tijdslot
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {!geselecteerdeDag && (
            <div style={{ textAlign: 'center', padding: 30, color: '#a19f9d', fontSize: 13 }}>
              Selecteer een datum om tijdslots te bekijken
            </div>
          )}
        </div>
      )}

      {/* ===== BOEKINGSSYSTEEM API VIEW ===== */}
      {viewMode === 'boekingapi' && (
        <div>
          <div style={{ border: '2px solid #0078D4', borderRadius: 12, overflow: 'hidden', marginBottom: 20 }}>
            <div style={{ background: '#0078D4', color: '#fff', padding: '14px 20px' }}>
              <div style={{ fontSize: 16, fontWeight: 700 }}>🔗 Boekingssysteem Integratie</div>
              <div style={{ fontSize: 12, opacity: 0.8, marginTop: 4 }}>
                Real-time beschikbaarheidsdata voor extern boekingssysteem
              </div>
            </div>
            <div style={{ padding: 20 }}>
              {/* Gereedheid checks */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Gereedheid</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {[
                    { ok: true, label: 'Beschikbaarheidservice actief', detail: 'Real-time berekening per medewerker, per dag, per tijdslot' },
                    { ok: true, label: 'Capaciteitscheck API', detail: `checkCapaciteit() — minimum bezetting: ${minimumBezetting} medewerkers` },
                    { ok: showroomMw.length > 0, label: `Showroom-medewerkers (${showroomMw.length})`, detail: showroomMw.length > 0 ? showroomMw.map(m => m.Naam).join(', ') : 'Niet geconfigureerd — markeer via Medewerkers' },
                    { ok: true, label: 'Werkrooster + verlof overlay', detail: 'Mutaties (verlof, ziek, flex/ADV) worden realtime meegenomen' },
                    { ok: true, label: 'Tijdslot capaciteit', detail: 'Ochtend (08:00-12:30) en middag (12:30-17:30) apart beschikbaar' },
                  ].map((item, i) => (
                    <div key={i} style={{ padding: '8px 12px', borderRadius: 8, background: item.ok ? '#dff6dd' : '#fde7e9', border: `1px solid ${item.ok ? '#a7e3a5' : '#f1b1b5'}`, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 14 }}>{item.ok ? '✅' : '❌'}</span>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600 }}>{item.label}</div>
                        <div style={{ fontSize: 11, color: '#605e5c' }}>{item.detail}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Hoe het werkt */}
              <div style={{ marginBottom: 20, padding: 16, background: '#faf9f8', borderRadius: 10, border: '1px solid #edebe9' }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>🔄 Hoe het boekingssysteem werkt</div>
                <div style={{ fontSize: 12, color: '#323130', lineHeight: 1.6 }}>
                  <div style={{ marginBottom: 6 }}><strong>1.</strong> Klant selecteert datum en tijdslot voor showroombezoek</div>
                  <div style={{ marginBottom: 6 }}><strong>2.</strong> Systeem checkt beschikbaarheid via <code style={{ background: '#e8f4fd', padding: '1px 4px', borderRadius: 3 }}>checkCapaciteit()</code></div>
                  <div style={{ marginBottom: 6 }}><strong>3.</strong> Alleen showroom-medewerkers (🏪) worden meegeteld</div>
                  <div style={{ marginBottom: 6 }}><strong>4.</strong> Bij onvoldoende capaciteit ({`< ${minimumBezetting}`} beschikbaar) → slot geblokkeerd</div>
                  <div style={{ marginBottom: 6 }}><strong>5.</strong> Werkrooster + verlof + ziek + flex/ADV + roosterwijzigingen → alles realtime meegenomen</div>
                  <div><strong>6.</strong> Events in Outlook tonen werkrooster als basis met mutaties duidelijk zichtbaar</div>
                </div>
              </div>

              {/* API voorbeeld */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>📦 API Data Voorbeeld</div>
                <pre style={{ background: '#1a1a2e', color: '#8bdbbb', padding: 16, borderRadius: 10, fontSize: 11, overflow: 'auto', maxHeight: 200, lineHeight: 1.5 }}>
{`{
  "datum": "${today}",
  "weekdag": "${new Date().toLocaleDateString('nl-NL', { weekday: 'long' })}",
  "totaalBeschikbaar": ${beschikbaarheidData[today]?.capaciteit?.aantalBeschikbaar ?? '?'},
  "capaciteitOk": ${beschikbaarheidData[today]?.capaciteit?.beschikbaar ?? 'false'},
  "tijdslots": [
    { "slot": "ochtend", "start": "08:00", "eind": "12:30", "beschikbaar": ${beschikbaarheidData[today]?.alle?.filter(b => b.beschikbaar).length ?? '?'}, "bookbaar": true },
    { "slot": "middag", "start": "12:30", "eind": "17:30", "beschikbaar": ${beschikbaarheidData[today]?.alle?.filter(b => b.beschikbaar).length ?? '?'}, "bookbaar": true }
  ]
}`}
                </pre>
              </div>

              {/* Export knop */}
              <button onClick={handleExportApi}
                style={{ padding: '10px 24px', borderRadius: 8, border: 'none', background: '#0078D4', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                📥 Exporteer beschikbaarheidsdata (JSON)
              </button>
              <span style={{ fontSize: 11, color: '#a19f9d', marginLeft: 12 }}>
                Komende {weken} weken, per dag, per tijdslot, per medewerker
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Dag detail panel (voor grid en tijdslot views) */}
      {geselecteerdeDag && dagDetail && viewMode !== 'boekingapi' && (
        <div style={{ marginTop: 20, border: '2px solid #0078D4', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ background: '#0078D4', color: '#fff', padding: '12px 16px', fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>📅 {new Date(geselecteerdeDag + 'T12:00:00').toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
            <button onClick={() => setGeselecteerdeDag(null)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12 }}>✕ Sluiten</button>
          </div>
          <div style={{ padding: 16 }}>
            <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
              <div style={{ padding: '10px 16px', borderRadius: 8, background: dagDetail.capaciteit.beschikbaar ? '#dff6dd' : '#fde7e9',
                border: `1px solid ${dagDetail.capaciteit.beschikbaar ? '#a7e3a5' : '#f1b1b5'}` }}>
                <span style={{ fontSize: 20 }}>{dagDetail.capaciteit.beschikbaar ? '✅' : '⚠️'}</span>
                <span style={{ fontSize: 14, fontWeight: 700, marginLeft: 8, color: dagDetail.capaciteit.beschikbaar ? '#107C10' : '#D13438' }}>
                  {dagDetail.capaciteit.aantalBeschikbaar} van {dagDetail.capaciteit.totaalMedewerkers} beschikbaar
                </span>
              </div>
              {showroomMw.length > 0 && (
                <div style={{ padding: '10px 16px', borderRadius: 8, background: '#fff4e6', border: '1px solid #ffe0b2' }}>
                  🏪 <span style={{ fontSize: 13, fontWeight: 600, color: '#E86E1C' }}>
                    {dagDetail.capaciteit.medewerkers.filter(m => showroomIds.includes(m.id)).length} showroom
                  </span>
                </div>
              )}
            </div>

            {dagDetail.capaciteit.medewerkers.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#107C10', marginBottom: 6 }}>🟢 Beschikbaar ({dagDetail.capaciteit.medewerkers.length})</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {dagDetail.capaciteit.medewerkers.map(m => (
                    <div key={m.id} style={{ padding: '6px 12px', borderRadius: 8, background: '#dff6dd', border: '1px solid #a7e3a5', fontSize: 12 }}>
                      {showroomIds.includes(m.id) && '🏪 '}<strong>{m.naam}</strong>
                      {m.werkTijden && <span style={{ color: '#605e5c', marginLeft: 6 }}>{m.werkTijden.startTijd}-{m.werkTijden.eindTijd}</span>}
                      <span style={{ color: '#107C10', marginLeft: 4 }}>({m.werkUren}u)</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {dagDetail.capaciteit.afwezig.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#D83B01', marginBottom: 6 }}>🔴 Afwezig / Mutatie ({dagDetail.capaciteit.afwezig.length})</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {dagDetail.capaciteit.afwezig.map(m => (
                    <div key={m.id} style={{ padding: '6px 12px', borderRadius: 8, background: '#fde7e9', border: '1px solid #f1b1b5', fontSize: 12 }}>
                      <strong>{m.naam}</strong>
                      <span style={{ color: '#D83B01', marginLeft: 6 }}>{m.reden}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ marginTop: 12, padding: '10px 14px', background: dagDetail.capaciteit.beschikbaar ? '#e8f4fd' : '#fde7e9', borderRadius: 8, fontSize: 12,
              color: dagDetail.capaciteit.beschikbaar ? '#0078D4' : '#D13438', border: `1px solid ${dagDetail.capaciteit.beschikbaar ? '#b4d6f0' : '#f1b1b5'}` }}>
              {dagDetail.capaciteit.beschikbaar
                ? `✅ Boekingssysteem: voldoende capaciteit voor showroombezoeken (${dagDetail.capaciteit.aantalBeschikbaar} beschikbaar, minimum: ${minimumBezetting})`
                : `🚫 Boekingssysteem: onvoldoende capaciteit — dit tijdslot wordt automatisch geblokkeerd (${dagDetail.capaciteit.aantalBeschikbaar} beschikbaar, minimum: ${minimumBezetting})`}
            </div>
          </div>
        </div>
      )}

      {/* Statistieken */}
      <div style={{ marginTop: 20, padding: 16, background: '#faf9f8', borderRadius: 10, border: '1px solid #edebe9' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#323130', marginBottom: 10 }}>📊 Capaciteitsoverzicht — {weken} weken</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
          {(() => {
            const stats = { ok: 0, laag: 0, geen: 0, totaal: 0 };
            Object.values(beschikbaarheidData).forEach(d => {
              if (!d.capaciteit) return;
              stats.totaal++;
              if (d.capaciteit.aantalBeschikbaar === 0) stats.geen++;
              else if (!d.capaciteit.beschikbaar) stats.laag++;
              else stats.ok++;
            });
            return (
              <>
                <div style={{ padding: '10px 14px', borderRadius: 8, background: '#dff6dd', textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 800, color: '#107C10' }}>{stats.ok}</div>
                  <div style={{ fontSize: 11, color: '#107C10' }}>✅ voldoende</div>
                </div>
                <div style={{ padding: '10px 14px', borderRadius: 8, background: '#fff4ce', textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 800, color: '#7A6400' }}>{stats.laag}</div>
                  <div style={{ fontSize: 11, color: '#7A6400' }}>⚠️ onder min.</div>
                </div>
                <div style={{ padding: '10px 14px', borderRadius: 8, background: '#fde7e9', textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 800, color: '#D13438' }}>{stats.geen}</div>
                  <div style={{ fontSize: 11, color: '#D13438' }}>🚫 geen cap.</div>
                </div>
                <div style={{ padding: '10px 14px', borderRadius: 8, background: '#e8f4fd', textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 800, color: '#0078D4' }}>{stats.totaal}</div>
                  <div style={{ fontSize: 11, color: '#0078D4' }}>werkdagen</div>
                </div>
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

function getWeekNummer(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  const start = new Date(d.getFullYear(), 0, 1);
  const diff = (d - start + ((start.getDay() + 6) % 7) * 86400000) / 86400000;
  return Math.ceil((diff + 1) / 7);
}
